import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Injector,
  Input,
  OnInit,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ContentChange, QuillEditorComponent, QuillModules, QuillService } from 'ngx-quill';
import { AbstractValueAccessor, MakeProvider } from '../abstract-value-accessor';
import setupIcons from './setup-icons.function';
import { fromEvent, switchMap, take } from 'rxjs';
import { SanitizerService, trimEmptyHtmlRows } from '@anecdotes/utils';
// This package enables smart past to quill editor. Without it copy/paste some formatted text could work incorrectly
import 'quill-paste-smart';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import Quill from 'quill';

export enum RichTextareaFeatures {
  FontStyle = 'FontStyle',
  List = 'List',
  Link = 'Link',
}

const QUILL_EDITOR_LIST_EVENT = 'list empty enter';

@Component({
  selector: 'app-rich-textarea',
  templateUrl: './rich-textarea.component.html',
  styleUrls: ['./rich-textarea.component.scss'],
  providers: [MakeProvider(RichTextareaComponent)],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RichTextareaComponent extends AbstractValueAccessor implements OnChanges, OnInit {
  @HostBinding('class')
  private readonly classes = 'flex flex-col';

  @ViewChild('quillEditor', { static: true })
  private quillEditor: QuillEditorComponent;

  private get toolbarConfig(): any[] {
    const result = [];
    const featuresSet = new Set(this.features);

    if (featuresSet.has(RichTextareaFeatures.FontStyle)) {
      result.push(['bold', 'underline']);
    }

    if (featuresSet.has(RichTextareaFeatures.List)) {
      result.push([{ list: 'ordered' }, { list: 'bullet' }]);
    }

    if (featuresSet.has(RichTextareaFeatures.Link)) {
      result.push(['link']);
    }

    result.push(['clean']);

    return result;
  }

  private get disabledEnterConfig(): Pick<QuillModules, 'keyboard'> {
    return {
      keyboard: {
        bindings: {
          handleEnter: {
            key: 13,
            handler: () => false,
          },
          [QUILL_EDITOR_LIST_EVENT]: {
            key: 13,
            collapsed: true,
            format: ['list'],
            empty: true,
            handler: () => false,
          },
        }
      },
    };
  }

  _features: RichTextareaFeatures[];

  @Input()
  label: string;

  @Input()
  labelIcon: string;

  @Input()
  labelParamsObj: any;

  @Input()
  placeholder: string;

  @Input()
  placeholderParamsObj: any;

  @Input()
  readonly = false;

  @Input()
  maxLength: number;

  @Input()
  minLength: number;

  @Input()
  required = false;

  @Input()
  focusAfterEditorCreated = false;

  @Input()
  focusEnabled = true;

  @Input()
  displayCharactersCounter = false;

  @Input()
  enterEnabled = true;

  @Input()
  shouldDisplayToolbar = true;

  @Input()
  preserveWhitespace: boolean;

  @Input()
  minTextareaHeight: number;

  get features(): RichTextareaFeatures[] {
    return this._features ?? [
      RichTextareaFeatures.FontStyle,
      RichTextareaFeatures.List,
      RichTextareaFeatures.Link,
    ];
  }

  @Input()
  set features(features: RichTextareaFeatures[]) {
    this._features = features;
  }

  isFocused: boolean;

  get modules(): QuillModules {
    return {
      toolbar: this.shouldDisplayToolbar ? this.toolbarConfig : false,
      ...(!this.enterEnabled && this.disabledEnterConfig ),
    };
  }

  protected internalFormControl = new FormControl();

  constructor(
    private readonly quillService: QuillService,
    private readonly sanitizer: SanitizerService,
    private readonly cd: ChangeDetectorRef,
    injector: Injector,
  ) {
    super(injector);
  }

  ngOnChanges(simpleChanges: SimpleChanges): void {
    super.ngOnChanges(simpleChanges);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.redefineSanitizeMethod();

    this.quillService.getQuill()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((quill) => setupIcons(quill));

    this.internalFormControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => (this.value = value));

    this.quillEditor.onEditorCreated
      .pipe(
        take(1),
        switchMap(() => fromEvent(this.quillEditor.quillEditor.scroll.domNode, 'focus')),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(this.focus.bind(this));
  }

  writeValue(value: any): void {
    value = this.sanitizer.sanitizeRichText(value);
    super.writeValue(value);
    this.internalFormControl.setValue(this.value, { emitEvent: false });
  }

  textChanged(content: ContentChange): void {
    const textLength = content.editor.getLength() - 1;

    if (textLength > this.maxLength) {
      content.editor.deleteText(this.maxLength, textLength);
    }
  }

  protected focus(): void {
    this.onTouch();

    if (this.focusEnabled) {
      this.isFocused = true;
    }

    this.cd.detectChanges();
  }

  protected blur(): void {
    if (this.focusEnabled) {
      this.isFocused = false;
    }

    this.writeValue(trimEmptyHtmlRows(this.internalFormControl.value));
    this.markAsDirty();
    this.cd.detectChanges();
  }

  private redefineSanitizeMethod(): void {
    const Link = Quill.import('formats/link');

    Link.sanitize = (url: string) => {
      // If the URL does not have http/https, prepend 'https://'
      if (!/^https?:\/\//.test(url)) {
        return `https://${url}`;
      }

      return url;
    };

    Quill.register(Link, true);
  }
}
